<template>
  <v-card class="my-1" elevation="0" style="height: 100%;">
    <div
      v-if="loading"
      class="d-flex align-center justify-center"
      style="height: 300px;"
    >
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <div
      v-else-if="!servicoSelecionado || !servicoSelecionado.codigos_servico"
      class="d-flex align-center justify-center grey--text text-center"
      style="height: 300px;"
    >
      <div>
        <div class="text-center">
          <v-icon large color="grey lighten-1">mdi-table-arrow-left</v-icon>
          <div class="mt-2 body-2">
            Clique em um serviço na tabela para ver detalhes
          </div>
        </div>
      </div>
    </div>

    <v-card v-else class="my-1" elevation="0" style="height: 100%;">
      <v-card-title>
        <h5>
          {{
            servicoSelecionado
              ? `${servicoSelecionado.indice_categoria} (${servicoSelecionado.codigos_servico})  — ${servicoSelecionado.descricao_por_indice}`
              : "—"
          }}
        </h5>
      </v-card-title>

      <v-card-text>
        <!-- Cards KPIs -->
        <v-row dense class="mb-3">
          <v-col cols="4">
            <v-card outlined elevation="0">
              <v-card-text>
                <div class="caption grey--text">PSFP</div>
                <div
                  class="text-h6 font-weight-bold"
                  :style="{ color: corFaixa(kpisConsolidado.faixa_chave) }"
                >
                  {{ kpisConsolidado.psfp | parseNumberToFloatBrIgnoringNaN }} %
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card outlined elevation="0">
              <v-card-text>
                <div class="caption grey--text">SA</div>
                <div class="text-h6 font-weight-bold">
                  {{ kpisConsolidado.sa | parseNumberToFloatBrIgnoringNaN }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card outlined elevation="0">
              <v-card-text>
                <div class="caption grey--text">SFP</div>
                <div class="text-h6 font-weight-bold">
                  {{ kpisConsolidado.sfp | parseNumberToFloatBrIgnoringNaN }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert
          v-if="kpisConsolidado.faixa_chave === 'acima_20'"
          type="warning"
          text
          dense
          class="mb-3 py-1"
        >
          PSFP alto com volume baixo (Sa={{ kpisConsolidado.sa }}) — possível
          efeito denominador.
        </v-alert>

        <!-- Gráfico de linha PSFP Mensal -->
        <div class="caption grey--text mb-1">PSFP — Mensal</div>
        <highcharts :options="chartPsfp" :deepCopyOnUpdate="false" />

        <v-divider class="my-4"></v-divider>

        <!-- Gráfico de barras Sa e SFP Mensal -->
        <div class="caption grey--text mb-1 mt-3">Sa e SFP — Mensal</div>
        <highcharts :options="chartSaSfp" :deepCopyOnUpdate="false" />
      </v-card-text>
    </v-card>
  </v-card>
</template>

<script>
import { highchartsColors } from "@/constants/highchartsColors";

export default {
  name: "DashboardIsfpPsfpKpisDetalhamento",
  props: {
    servicoSelecionado: { type: Object, default: null },
    competencia: { type: String, required: true },
    loading: { type: Boolean, default: false },
  },
  computed: {
    serie() {
      return this.servicoSelecionado?.serie ?? [];
    },
    kpisConsolidado() {
      return {
        psfp: this.servicoSelecionado?.psfp ?? 0,
        sa: this.servicoSelecionado?.sa ?? 0,
        sfp: this.servicoSelecionado?.sfp ?? 0,
        faixa_chave: this.servicoSelecionado?.faixa_chave ?? "ate_1",
      };
    },
    categorias() {
      return this.serie.map((s) =>
        s.competencia ? this.formatarCompetencia(s.competencia) : "",
      );
    },
    chartPsfp() {
      return {
        chart: { type: "line", height: 200 },
        credits: { enabled: false },
        title: { text: null },
        legend: { enabled: false },
        xAxis: {
          categories: this.categorias,
          labels: { style: { fontSize: "11px" } },
        },
        yAxis: {
          title: { text: null },
          labels: {
            style: { fontSize: "11px" },
            formatter() {
              return `${this.value}%`;
            },
          },
        },
        tooltip: {
          formatter() {
            return `<b>${this.x}</b><br/>PSFP: ${this.y} %`;
          },
        },
        series: [
          {
            name: "PSFP",
            animation: false,
            color: highchartsColors[0],
            marker: { symbol: "circle", radius: 3 },
            data: this.serie.map((s) => s.psfp ?? null),
          },
        ],
        lang: { noData: "Sem dados para exibição" },
      };
    },
    chartSaSfp() {
      return {
        chart: { type: "column", height: 200 },
        credits: { enabled: false },
        title: { text: null },
        legend: { enabled: true },
        xAxis: {
          categories: this.categorias,
          labels: { style: { fontSize: "11px" } },
        },
        yAxis: {
          title: { text: null },
          labels: { style: { fontSize: "11px" } },
        },
        tooltip: { shared: true },
        plotOptions: {
          column: { dataLabels: { enabled: false } },
        },
        series: [
          {
            name: "Sa",
            animation: false,
            color: highchartsColors[0],
            data: this.serie.map((s) => s.sa ?? 0),
          },
          {
            name: "SFP",
            animation: false,
            color: highchartsColors[1],
            data: this.serie.map((s) => s.sfp ?? 0),
          },
        ],
        lang: { noData: "Sem dados para exibição" },
      };
    },
  },
  methods: {
    formatarCompetencia(competencia) {
      const [ano, mes] = competencia.split("-");
      const meses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      return `${meses[parseInt(mes, 10) - 1]}/${ano.slice(2)}`;
    },
    getFaixaChave(psfp) {
      if (psfp <= 1) return "ate_1";
      if (psfp <= 5) return "de_1_a_5";
      if (psfp <= 10) return "de_5_a_10";
      if (psfp <= 20) return "de_10_a_20";
      return "acima_20";
    },
    corFaixa(faixaChave) {
      const cores = {
        ate_1: "#4CAF50",
        de_1_a_5: "#8BC34A",
        de_5_a_10: "#FFC107",
        de_10_a_20: "#FF9800",
        acima_20: "#F44336",
      };
      return cores[faixaChave] ?? "#000";
    },
  },
};
</script>
